import Box from "@jewlr/storybook/core/box"
import Flex from "@jewlr/storybook/core/flex"
import Image from "@jewlr/storybook/core/image"
import loadable from "@loadable/component"
import { AnimatePresence, motion } from "framer-motion"
import PropTypes from "prop-types"
import React, { useEffect, useRef, useState } from "react"
import { connect } from "react-redux"
import { withRouter } from "react-router-dom"
import ScrollLock, {
  TouchScrollable,
} from "react-scrolllock-with-horizontal-scroll"
import styled from "styled-components"

import { toggleCartPreview } from "areas/cart/store/actions"
import { retry } from "helpers/application"

const PopupContent = loadable(() => retry(() => import("../popup/content")))
const Cta = loadable(() => retry(() => import("../popup/cta")))

const EmptyCart = loadable(() => retry(() => import("./empty-cart")))
const Header = loadable(() => retry(() => import("./header")))

const Overlay = styled.div`
  background-color: rgba(0, 0, 0, 0.1);
  bottom: 0;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: ${(props) => props.theme.zIndices.modalBackground};
`
const Transition = styled(motion.div).attrs(() => ({
  animate: "show",
  exit: "hide",
  initial: "hide",
  transition: {
    type: "linear",
  },
  variants: {
    hide: {
      right: "-100%",
      transition: { duration: 0.5 },
    },
    show: {
      right: 0,
      transition: { duration: 0.5 },
    },
  },
}))`
  -webkit-overflow-scrolling: touch;
  ::-webkit-scrollbar {
    background: transparent;
    width: 0px;
  }
  background-color: ${(props) => props.theme.colors.white};
  bottom: 0;
  height: 100%;
  max-width: 375px;
  overflow-y: auto;
  position: fixed;
  right: 0;
  width: 88%;
  z-index: ${(props) => props.theme.zIndices.modal};
`
const Sidebar = styled.div`
  background-color: rgb(255, 255, 255);
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: none;
  overflow-y: hidden;
  padding-top: 12px;
  position: relative;
  transition: all 0.2s ease 0s;
  width: 100%;
`
const TouchContent = styled.div`
  -webkit-overflow-scrolling: touch;
  flex: 1 1 auto;
  overflow-x: hidden;
  overflow-y: auto;
  padding-bottom: 0;
  ${(props) => props.theme.mediaQueries.tablet`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow-y: hidden;
    padding-bottom: 32px;
  `}
`
const CtaContainer = styled(Box)`
  background-color: rgb(255, 255, 255);
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  padding-bottom: 24px;
  padding-left: 16px;
  padding-right: 16px;
  position: absolute;
  top: 46px;
  width: 100%;
  z-index: 2;
`

const InternalCartPreviewModal = ({
  cartLoaded,
  closeCartPreview,
  history,
  lineItems,
  showSidebar,
}) => {
  const [sticky, setSticky] = useState(false)
  const ctaRef = useRef()
  const contentRef = useRef()

  useEffect(() => {
    if (history.action !== "REPLACE" && showSidebar) {
      closeCartPreview()
    }
  }, [history.location.pathname])

  useEffect(() => {
    if (!showSidebar) {
      setSticky(false)
    }
  }, [showSidebar])

  useEffect(() => {
    if (contentRef && contentRef.current) {
      const content = contentRef.current
      const checkScrollPosition = () => {
        if (ctaRef && ctaRef.current) {
          const cta = ctaRef.current
          const rect = cta.getBoundingClientRect()
          const offsetTop = content.offsetTop
          if (rect) {
            setSticky(offsetTop > rect.top)
          }
        }
      }
      content.addEventListener("scroll", checkScrollPosition)
      return () => content.removeEventListener("scroll", checkScrollPosition)
    }
  })

  return (
    <>
      <AnimatePresence>
        {showSidebar && (
          <Transition>
            <ScrollLock isActive>
              <Sidebar data-cy="cart-atc-modal">
                <Header />
                {sticky && (
                  <CtaContainer>
                    <Cta />
                  </CtaContainer>
                )}
                <TouchScrollable>
                  <TouchContent ref={contentRef}>
                    {cartLoaded ? (
                      <>
                        {lineItems?.length > 0 ? (
                          <Flex flexDirection="column" px={2}>
                            <PopupContent ctaRef={ctaRef} sticky={sticky} />
                          </Flex>
                        ) : (
                          <EmptyCart />
                        )}
                      </>
                    ) : (
                      <Box textAlign="center">
                        <Image
                          data-cy="collection-loading-icon"
                          height="150"
                          margin="30px auto"
                          src={require("images/layout/loadinglarge.gif")}
                          width="150"
                        />
                      </Box>
                    )}
                  </TouchContent>
                </TouchScrollable>
              </Sidebar>
            </ScrollLock>
          </Transition>
        )}
      </AnimatePresence>
      {showSidebar && <Overlay onClick={() => closeCartPreview()} />}
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    cartLoaded: state.cart.cartLoaded,
    lineItems: state.cart.line_items,
    showSidebar: state.cart.showSidebar,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    closeCartPreview: () => {
      dispatch(toggleCartPreview())
    },
  }
}

InternalCartPreviewModal.propTypes = {
  cartLoaded: PropTypes.bool,
  closeCartPreview: PropTypes.func,
  history: PropTypes.object,
  lineItems: PropTypes.array,
  location: PropTypes.object,
  showSidebar: PropTypes.bool,
}

const CartPreviewModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(InternalCartPreviewModal)

export default withRouter(CartPreviewModal)
